/**
 * Drives Milestone A1's "summon an agent" copy/paste loop for one agent [SocialClient].
 *
 * The summoned agent is a real, separate actor (its own identity), wired to the human's node so its commitments
 * replicate like any peer's — in the browser over an in-process [com.oasisfeng.invaluable.p2p.core.LoopbackSession]. So
 * the agent reads the same local world a human sees ([prompt]) and acts through the same verbs a human uses ([execute]);
 * the copy/paste step merely carries a [prompt] out to any LLM and the LLM's reply back into [preview].
 *
 * Multi-turn: the first [prompt] is self-contained (role + world + full grammar); after a round is [execute]d, later
 * prompts are continuations that lean on the LLM chat's existing context (refreshed world only). [preview] parses a
 * pasted reply into one item per line so a UI can confirm or reject each before [execute] authors the confirmed ones.
 *
 * Not thread-safe: confine to one thread (the UI thread, in the browser).
 */

package com.oasisfeng.invaluable.social

class SummonSession(private val agent: SocialClient) {

    /** Rounds already [execute]d. Zero means the next [prompt] is the self-contained first turn. */
    var turns: Int = 0
        private set

    /** The agent's own view tag, so a UI can show whose identity will author the confirmed actions. */
    val agentLabel: String get() = agent.board().me.label

    /**
     * Prompt text to copy into an LLM. [focus] optionally directs the agent's attention at one visible post/thread;
     * the rest of the local world is still shown so the agent reasons in context.
     */
    fun prompt(focus: SocialRef? = null): String {
        val note = focus?.let { ref ->
            agent.board().card(ref)
                ?.let { "Focus on this post: [${it.ref}] \"${it.text ?: "(non-text content)"}\". Consider replying to, backing, or recommending it." }
                ?: "Focus on item [$ref] (not currently visible to you)."
        }
        val continuing = turns > 0
        return agent.context(note = note, continuing = continuing) + responseProtocol(continuing)
    }

    /**
     * The copy/paste response contract appended after the world: the summoned LLM must reply with [ActionGrammar] lines
     * that [preview]/[execute] parse. This lives here, not in [ContextRenderer], because it is specific to the paste
     * flow — the active agent (MCP/CLI) instead acts through tool calls and must not see this grammar. On a
     * continuation the chat already holds the grammar, so only a one-line reminder is restated.
     */
    private fun responseProtocol(continuing: Boolean): String = buildString {
        appendLine()
        if (continuing) {
            appendLine("## Respond with action lines only — same grammar as before, one per line.")
        } else {
            appendLine("## Actions you can take")
            appendLine("Respond with only action lines, one per line (blank lines and lines starting with # are ignored). If nothing is worth doing, respond with no action lines.")
            ActionGrammar.syntax.forEach { appendLine(it) }
        }
    }

    /** Parse a pasted LLM reply into one preview item per actionable line; blank lines and `#` comments are dropped. */
    fun preview(pasted: String): List<AgentActionPreview> =
        pasted.lineSequence()
            .map(String::trim)
            .filter { it.isNotEmpty() && !it.startsWith("#") }
            .map { line -> AgentActionPreview(line, ActionGrammar.parse(line)) }
            .toList()

    /**
     * Author the confirmed [actions] as the agent, returning each outcome in order, and advance the turn counter so the
     * next [prompt] is a continuation. Authoring routes through the agent's [SocialClient], so each action replicates to
     * the human's node (and onward to any of the human's peers) exactly as a remote author's would.
     */
    fun execute(actions: List<SocialAction>): List<ActionOutcome> {
        val outcomes = actions.map(agent::perform)
        turns++
        return outcomes
    }
}

/** One pasted line and its parse result, for a per-line confirm/reject preview UI. */
data class AgentActionPreview(val line: String, val parsed: ParsedAction) {
    /** The action when the line parsed cleanly, else null (an [executable] of false). */
    val action: SocialAction? get() = (parsed as? ParsedAction.Ok)?.action
    val executable: Boolean get() = action != null
    /** A short, human-readable note: the parse error, or a confirmation of what the action will do. */
    val note: String get() = when (parsed) {
        is ParsedAction.Error -> parsed.message
        is ParsedAction.Ok -> "ok"
    }
}
